import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { DashboardLayout, Card } from '../components';
import { AdminDailyStats } from '../components/AdminDailyStats';
import { AdminPayoutAlerts } from '../components/AdminPayoutAlerts';
import { Breadcrumb } from '../components/Breadcrumb';
import { useAuth } from '../context/AuthContext';
import { adminApi, notificationApi } from '../services/api';
import { loadGlobalTemplate, type StatusStep } from '../services/projectStatusUtils';
import type { AdminHomeStats, ProjectStatusCounts } from '../types';

const badgeToHex: Record<string, string> = {
  secondary: '#6c757d',
  info: '#17a2b8',
  primary: '#007bff',
  warning: '#ffc107',
  success: '#28a745',
  danger: '#dc3545'
};

const fallbackStatusColors: Record<string, string> = {
  not_started: '#6c757d',
  contract: '#17a2b8',
  payment: '#ffc107',
  in_progress: '#007bff',
  milestone: '#28a745',
  on_hold: '#fd7e14',
  completed: '#28a745',
  objection: '#e8890c',
  conflict: '#dc3545',
  dispute: '#b02a37'
};

export function AdminHome() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [includeTests, setIncludeTests] = useState(false);
  const [stats, setStats] = useState<AdminHomeStats | null>(null);
  const [projectCounts, setProjectCounts] = useState<ProjectStatusCounts | null>(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [adminNotifications, setAdminNotifications] = useState<any[]>([]);
  const [templateSteps, setTemplateSteps] = useState<StatusStep[]>([]);

  useEffect(() => {
    let cancelled = false;
    setStats(null);
    adminApi.getHomeStats(includeTests).then(data => { if (!cancelled) setStats(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [includeTests]);

  useEffect(() => {
    notificationApi.getMyNotifications().then(data => setAdminNotifications(data.slice(0, 8))).catch(() => {});
    loadGlobalTemplate().then(steps => setTemplateSteps(steps)).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setProjectCounts(null);
    adminApi.getProjectsByStatus(fromDate || undefined, toDate || undefined, includeTests)
      .then(data => { if (!cancelled) setProjectCounts(data); })
      .catch(error => console.error('Failed to load project counts:', error));
    return () => { cancelled = true; };
  }, [fromDate, toDate, includeTests]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(amount) + ' kr';

  const kpiCards: Array<{ label: string; value: string; icon: string; color: string }> = stats
    ? [
        {
          label: t('adminHome.kpi.totaltAnbudsvarde'),
          value: formatCurrency(stats.totaltAnbudsvarde || 0),
          icon: 'dollar-sign',
          color: 'warning'
        },
        {
          label: t('adminHome.kpi.inbetaltReserverat'),
          value: formatCurrency(stats.inbetaltReserverat || 0),
          icon: 'check-circle',
          color: 'success'
        },
        {
          label: t('adminHome.kpi.vantarPaInbetalning'),
          value: formatCurrency(stats.vantarPaInbetalning || 0),
          icon: 'clock',
          color: (stats.vantarPaInbetalning || 0) > 0 ? 'danger' : 'success'
        },
        {
          label: t('adminHome.kpi.totaltAntalKunder'),
          value: String(stats.totaltAntalKunder || 0),
          icon: 'users',
          color: 'primary'
        },
        {
          label: t('adminHome.kpi.totaltAntalForetag'),
          value: String(stats.totaltAntalForetag || 0),
          icon: 'briefcase',
          color: 'info'
        },
        {
          label: t('adminHome.kpi.etappioNettomarginal'),
          value: formatCurrency(stats.etappioNettomarginal || 0),
          icon: 'trending-up',
          color: (stats.etappioNettomarginal || 0) >= 0 ? 'success' : 'danger'
        },
        {
          label: t('adminHome.kpi.nyaProjekt48h'),
          value: String(stats.nyaProjekt48h || 0),
          icon: 'folder-plus',
          color: (stats.nyaProjekt48h || 0) > 0 ? 'success' : 'secondary'
        },
        {
          label: t('adminHome.kpi.nyaKunder48h'),
          value: String(stats.nyaKunder48h || 0),
          icon: 'user-plus',
          color: (stats.nyaKunder48h || 0) > 0 ? 'success' : 'secondary'
        },
        {
          label: t('adminHome.kpi.nyaForetag48h'),
          value: String(stats.nyaForetag48h || 0),
          icon: 'plus-square',
          color: (stats.nyaForetag48h || 0) > 0 ? 'success' : 'secondary'
        }
      ]
    : [];

  const getMaxCount = () => {
    if (!projectCounts) return 1;
    const values = Object.values(projectCounts);
    return Math.max(...values, 1);
  };

  // Build statusLabels dynamically from template steps with i18n fallback
  // The API counts an objection on its own, outside the status template (ClickUp 869fh4948).
  const statusLabels: Record<string, string> = templateSteps.length > 0
    ? { ...Object.fromEntries(templateSteps.map(s => [s.key, s.label_sv])), objection: t('adminHome.chart.objection') }
    : {
        not_started: 'Ej Startat',
        contract: 'Kontrakt',
        payment: 'Insättning',
        in_progress: 'Pagaende',
        milestone: 'Etappmal',
        on_hold: 'Vilande',
        completed: 'Avslutat',
        objection: t('adminHome.chart.objection'),
        conflict: 'Konflikt',
        dispute: 'Tvist'
      };

  // Build statusColors dynamically from template steps badge colors
  const statusColors: Record<string, string> = templateSteps.length > 0
    ? {
        ...Object.fromEntries(
          templateSteps.map(s => [s.key, badgeToHex[s.badgeColor] || fallbackStatusColors[s.key] || '#6c757d'])
        ),
        objection: fallbackStatusColors.objection
      }
    : fallbackStatusColors;

  if (!projectCounts) {
    return (
      <DashboardLayout>
        <AdminPayoutAlerts />
      <div className="form-check mb-3">
        <input id="kpi-include-tests" className="form-check-input" type="checkbox" checked={includeTests} onChange={event => setIncludeTests(event.target.checked)} />
        <label className="form-check-label" htmlFor="kpi-include-tests">{t('testProjects.includeKpis')}</label>
        <p className="text-explain mb-0">{t(includeTests ? 'testProjects.kpiAll' : 'testProjects.kpiOrdinary')}</p>
      </div>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Personal greeting header: the logged-in admin's name and the number of
  // ongoing projects platform-wide (every status except not-started/completed).
  const greetingName = user?.fullName || t('home.greeting.fallbackName', 'där');
  const ongoingProjectsCount = (Object.keys(projectCounts) as Array<keyof ProjectStatusCounts>).reduce(
    (sum, status) => (status === 'not_started' || status === 'completed' ? sum : sum + (projectCounts[status] || 0)),
    0
  );

  return (
    <DashboardLayout>
      <AdminPayoutAlerts />
      <div className="form-check mb-3">
        <input id="kpi-include-tests" className="form-check-input" type="checkbox" checked={includeTests} onChange={event => setIncludeTests(event.target.checked)} />
        <label className="form-check-label" htmlFor="kpi-include-tests">{t('testProjects.includeKpis')}</label>
        <p className="text-explain mb-0">{t(includeTests ? 'testProjects.kpiAll' : 'testProjects.kpiOrdinary')}</p>
      </div>
      {/* Personal greeting header on a white card: breadcrumb, "Hej {namn}"
          and the number of ongoing projects. */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="home-greeting">
            <Breadcrumb items={[{ label: t('nav.home') }]} />
            <h3 className="fw-bold mb-1 mt-2 text-break">
              {t('home.greeting.hello', { name: greetingName, defaultValue: 'Hej {{name}}' })}
            </h3>
            <p className="text-muted mb-0">
              {t('home.greeting.ongoingProjects', { count: ongoingProjectsCount, defaultValue: '{{count}} pågående projekt' })}
            </p>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* Left column: KPIs + Chart (2/3) */}
        <div className="col-lg-8">
          {/* Platform-wide KPI cards — always 5 per row at xl, wraps cleanly below */}
          {kpiCards.length > 0 && (
            <div className="row g-3 mb-4 row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-xl-5">
              {kpiCards.map(kpi => (
                <div key={kpi.label} className="col">
                  <div className="card h-100">
                    <div className="card-body p-3">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <i className={`feather-${kpi.icon} text-${kpi.color}`} style={{ fontSize: 14 }}></i>
                        <small className="fw-semibold text-muted kpi-label">{kpi.label}</small>
                      </div>
                      <div className={`fw-bold fs-4 text-${kpi.color}`}>{kpi.value}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Bar Chart - Projects per Status */}
          <Card
            title={t('adminHome.chart.title')}
            headerAction={
              <div className="d-flex gap-3 align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label mb-0 text-muted small">{t('adminHome.chart.fromDate')}:</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    style={{ width: '150px' }}
                  />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label mb-0 text-muted small">{t('adminHome.chart.toDate')}:</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    style={{ width: '150px' }}
                  />
                </div>
              </div>
            }
          >
            <div className="py-3">
              {projectCounts && (
                <>
                  {/* Vertical Bar Chart */}
                  <div className="d-flex justify-content-around align-items-end" style={{ height: '280px' }}>
                    {Object.entries(projectCounts).map(([status, count]) => {
                      const maxCount = getMaxCount();
                      const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
                      const barHeight = Math.max(percentage * 2.2, count > 0 ? 20 : 5);

                      return (
                        <div
                          key={status}
                          className="d-flex flex-column align-items-center justify-content-end h-100"
                          style={{ flex: 1, maxWidth: '100px', cursor: 'pointer' }}
                          onClick={() => navigate(`/admin/projects?status=${status}`)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && navigate(`/admin/projects?status=${status}`)}
                        >
                          <div className="fw-bold mb-2">{count}</div>
                          <div
                            style={{
                              width: '50px',
                              height: `${barHeight}px`,
                              backgroundColor: statusColors[status],
                              borderRadius: '4px 4px 0 0',
                              transition: 'height 0.5s ease-in-out'
                            }}
                          ></div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Labels below chart */}
                  <div className="d-flex justify-content-around border-top pt-3">
                    {Object.keys(projectCounts).map(status => (
                      <div
                        key={status}
                        className="text-center small text-muted"
                        style={{ flex: 1, maxWidth: '100px', fontSize: '11px' }}
                      >
                        {statusLabels[status] || status}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Legend */}
              <div className="d-flex flex-wrap justify-content-center gap-3 mt-4 pt-3 border-top">
                {Object.keys(projectCounts || {}).map(status => (
                  <div key={status} className="d-flex align-items-center gap-2">
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: statusColors[status] || '#6c757d',
                        borderRadius: '2px'
                      }}
                    ></div>
                    <span className="small text-muted">{statusLabels[status] || status}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Right column: Notifications (1/3) */}
        <div className="col-lg-4">
          <div className="card stretch stretch-full">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">
                <i className="feather-bell me-2" aria-hidden="true"></i>
                {t('notifications.title')}
              </h5>
              <Link to="/admin/notifications?tab=log" className="btn btn-sm btn-light-primary">
                {t('notifications.viewAll')}
              </Link>
            </div>
            <div className="card-body custom-card-action p-0">
              {adminNotifications.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="feather-bell-off fs-3 d-block mb-2"></i>
                  <small>{t('notifications.noNotifications')}</small>
                </div>
              ) : (
                <ul className="list-unstyled mb-0 activity-feed-1" style={{ padding: '1rem 1.25rem' }}>
                  {adminNotifications.map(n => {
                    const colorMap: Record<string, string> = { primary: 'primary', secondary: 'secondary', success: 'success', danger: 'danger', warning: 'warning', info: 'info' };
                    const feedColor = colorMap[n.metadata?.color] || 'primary';
                    return (
                      <li key={n.id} className={`feed-item feed-item-${feedColor}`}>
                        <div className="d-flex gap-3 justify-content-between">
                          <div style={{ minWidth: 0 }}>
                            <div className={`mb-1 text-truncate-1-line ${!n.read ? 'fw-bold' : ''}`}>
                              {n.title}
                            </div>
                            <p
                              className="fs-12 text-muted mb-0 text-truncate-2-line"
                              title={n.body}
                              style={{ cursor: 'default' }}
                            >
                              {n.body}
                            </p>
                          </div>
                          <div className="fs-12 fw-medium text-uppercase text-muted text-nowrap flex-shrink-0">
                            {new Date(n.createdAt).toLocaleString('sv-SE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      <AdminDailyStats includeTests={includeTests} />
    </DashboardLayout>
  );
}
